/**
 * Draw-tool controls (thickness + color), shown while draw mode is on. A
 * floating pill in the top-center of the canvas — screen space, above the
 * capture surface so its buttons stay clickable while the tool is active.
 * Color validation reuses core/color.ts's `normalizeHexColor` (the only writer
 * of a pen color into the doc is `session.createDrawingElement`, so this is
 * the gate).
 */
import { useState } from "react";
import { useStore } from "zustand";

import { normalizeHexColor } from "../../core/color.js";
import {
  PEN_PRESETS,
  THICKNESS_PRESETS,
  type DrawUiStore,
} from "./store.js";

export interface DrawToolbarProps {
  draw: DrawUiStore;
}

export function DrawToolbar({ draw }: DrawToolbarProps) {
  const mode = useStore(draw, (s) => s.mode);
  const color = useStore(draw, (s) => s.color);
  const thickness = useStore(draw, (s) => s.thickness);
  const [hex, setHex] = useState("");
  const [invalid, setInvalid] = useState(false);

  if (!mode) return null;

  const applyHex = () => {
    const normalized = normalizeHexColor(hex);
    if (normalized === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    draw.getState().setColor(normalized);
  };

  return (
    <div className="draw-toolbar" role="group" aria-label="Drawing pen">
      <span className="draw-toolbar-label">Pen</span>
      <div className="draw-thickness" role="group" aria-label="Thickness">
        {THICKNESS_PRESETS.map((t) => (
          <button
            key={t}
            type="button"
            className={`draw-thickness-btn${thickness === t ? " selected" : ""}`}
            data-tip={`${t} px`}
            aria-label={`${t} px`}
            aria-pressed={thickness === t}
            onClick={() => draw.getState().setThickness(t)}
          >
            <span
              className="draw-thickness-dot"
              style={{ width: Math.min(18, t + 2), height: Math.min(18, t + 2), background: color }}
            />
          </button>
        ))}
      </div>
      <div className="draw-sep" />
      <div className="draw-colors" role="group" aria-label="Color">
        {PEN_PRESETS.map((c) => (
          <button
            key={c}
            type="button"
            className={`draw-color-swatch${color === c ? " selected" : ""}`}
            style={{ background: c }}
            data-tip={c}
            aria-label={c}
            aria-pressed={color === c}
            onClick={() => draw.getState().setColor(c)}
          />
        ))}
        <input
          className={`draw-hex-input${invalid ? " invalid" : ""}`}
          type="text"
          value={hex}
          placeholder={color}
          spellCheck={false}
          aria-label="Custom hex color"
          aria-invalid={invalid}
          onChange={(e) => {
            setHex(e.target.value);
            if (invalid) setInvalid(false);
          }}
          onKeyDown={(e) => {
            e.stopPropagation(); // keep pen keys out of canvas shortcuts
            if (e.key === "Enter") {
              e.preventDefault();
              applyHex();
            }
          }}
        />
      </div>
    </div>
  );
}
